/**
 * Care Self - Shopify Theme JavaScript
 * Native Vanilla JS without external dependencies
 */

class CareSelfTheme {
  constructor() {
    this.initAccordions();
    this.initGallery();
    this.initSupplySelector();
    this.initQuantitySteppers();
    this.initCartDrawer();
    this.initMobileNav();
    this.initThemeEditorEvents();
  }

  /* Accordion Toggles */
  initAccordions() {
    document.querySelectorAll('.accordion-trigger').forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        const item = trigger.closest('.accordion-item');
        const isOpen = item.classList.contains('is-open');
        
        // Optional: close other accordions in the same group
        const group = item.closest('.accordion-group');
        if (group && group.dataset.singleOpen === 'true') {
          group.querySelectorAll('.accordion-item').forEach(i => i.classList.remove('is-open'));
        }

        if (isOpen) {
          item.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /* Product Media Gallery */
  initGallery() {
    const mainImg = document.querySelector('.product-gallery__main img');
    const thumbs = document.querySelectorAll('.product-gallery__thumb');
    
    if (!mainImg || thumbs.length === 0) return;

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const targetSrc = thumb.dataset.targetSrc;
        const targetAlt = thumb.dataset.targetAlt;
        if (targetSrc) {
          mainImg.src = targetSrc;
          if (targetAlt) mainImg.alt = targetAlt;
        }
        thumbs.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  }

  /* Custom Supply Bundle Selector */
  initSupplySelector() {
    const cards = document.querySelectorAll('.supply-option-card');
    const masterSelect = document.querySelector('[name="id"]');
    const addBtnText = document.querySelector('.btn-add-to-bag .btn-text');

    if (cards.length === 0) return;

    cards.forEach(card => {
      card.addEventListener('click', () => {
        cards.forEach(c => c.classList.remove('is-selected'));
        card.classList.add('is-selected');

        const variantId = card.dataset.variantId;
        const variantPrice = card.dataset.variantPrice;

        if (masterSelect && variantId) {
          masterSelect.value = variantId;
          masterSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (addBtnText && variantPrice) {
          addBtnText.textContent = `Add to bag — ${variantPrice}`;
        }
      });
    });
  }

  /* Quantity Stepper (+ / -) */
  initQuantitySteppers() {
    document.querySelectorAll('.quantity-stepper').forEach(stepper => {
      const input = stepper.querySelector('.quantity-input');
      const btnMinus = stepper.querySelector('.quantity-btn--minus');
      const btnPlus = stepper.querySelector('.quantity-btn--plus');

      if (!input) return;

      if (btnMinus) {
        btnMinus.addEventListener('click', () => {
          const currentVal = parseInt(input.value, 10) || 1;
          if (currentVal > 1) {
            input.value = currentVal - 1;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      if (btnPlus) {
        btnPlus.addEventListener('click', () => {
          const currentVal = parseInt(input.value, 10) || 1;
          input.value = currentVal + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    });
  }

  /* Cart Drawer & AJAX Actions */
  initCartDrawer() {
    const drawer = document.querySelector('.cart-drawer');
    const overlay = document.querySelector('.cart-drawer-overlay');
    const openBtns = document.querySelectorAll('[data-open-cart-drawer]');
    const closeBtns = document.querySelectorAll('[data-close-cart-drawer]');

    if (!drawer) return;

    const openDrawer = () => {
      drawer.classList.add('is-open');
      if (overlay) overlay.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      this.refreshCartDrawer();
    };

    const closeDrawer = () => {
      drawer.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-active');
      document.body.style.overflow = '';
    };

    openBtns.forEach(btn => btn.addEventListener('click', (e) => {
      e.preventDefault();
      openDrawer();
    }));

    closeBtns.forEach(btn => btn.addEventListener('click', (e) => {
      e.preventDefault();
      closeDrawer();
    }));

    if (overlay) {
      overlay.addEventListener('click', closeDrawer);
    }

    // Intercept AJAX add to cart forms
    document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const submitBtn = form.querySelector('[type="submit"], .btn-add-to-bag');
        const originalText = submitBtn ? submitBtn.innerHTML : '';
        
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.innerHTML = '<span>Adding...</span>';
        }

        const formData = new FormData(form);

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          });

          if (response.ok) {
            openDrawer();
          } else {
            // Fallback standard submit if error
            form.submit();
          }
        } catch (err) {
          console.warn('AJAX cart add error, falling back to standard submit:', err);
          form.submit();
        } finally {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.innerHTML = originalText;
          }
        }
      });
    });
  }

  /* Fetch live cart data and re-render drawer */
  async refreshCartDrawer() {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return;
      const cart = await res.json();
      
      // Update badge counts
      document.querySelectorAll('.cart-count-badge').forEach(badge => {
        badge.textContent = cart.item_count;
        badge.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });

      // Update Shipping Bar
      const threshold = 5000; // 50.00
      const remaining = threshold - cart.total_price;
      const fill = document.querySelector('.cart-shipping-bar__fill');
      const text = document.querySelector('.cart-shipping-bar__text');
      
      if (fill && text) {
        const percent = Math.min(100, Math.round((cart.total_price / threshold) * 100));
        fill.style.width = `${percent}%`;
        if (remaining <= 0) {
          text.textContent = "You've unlocked Free UK Delivery!";
        } else {
          const formattedRemaining = (remaining / 100).toFixed(2);
          text.textContent = `Add £${formattedRemaining} more for Free UK Delivery`;
        }
      }
    } catch (e) {
      console.log('Cart fetch failed (standard in development preview)');
    }
  }

  /* Mobile Navigation Drawer */
  initMobileNav() {
    const toggle = document.querySelector('.mobile-nav-toggle');
    const mobileMenu = document.querySelector('.mobile-menu-drawer');
    const closeBtn = document.querySelector('.mobile-menu-close');

    if (!toggle || !mobileMenu) return;

    toggle.addEventListener('click', () => {
      mobileMenu.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        mobileMenu.classList.remove('is-open');
        document.body.style.overflow = '';
      });
    }
  }

  /* Shopify Theme Editor Hooks */
  initThemeEditorEvents() {
    document.addEventListener('shopify:section:load', () => {
      this.initAccordions();
      this.initGallery();
      this.initSupplySelector();
      this.initQuantitySteppers();
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.careSelfTheme = new CareSelfTheme();
});
